import { formats } from "@/utils/formats";
import { formatSymbol } from "@/utils/i18n/format-symbol";

export const faq = {
  zh: {
    "when-is-symbol-latest-earnings-report-released": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的最新財報發佈時間？`,
      content: (values: (string | number)[]) =>
        `${values[0]} (${formatSymbol(values[1])}) 最近一期的財務報告為 ${
          values[2]
        }，於 ${
          values[3]
        } 對外公布。該財報為依據 IFRS / US GAAP 編製之合併財報，涵蓋營收、獲利能力、現金流量與資本結構等主要財務指標，為評估企業短期經營績效與財務體質的核心依據。`,
    },
    "where-does-symbol-fall-in-the-pe-river-chart": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 在本益比河流圖裡位在哪個區間？`,
      content: (values: (string | number)[]) =>
        `根據歷史估值區間分析，目前 ${values[0]} (${formatSymbol(
          values[1]
        )}) 的本益比（P/E Ratio）是 ${formats.decimal(
          values[2] as number
        )}，在本益比河流圖中屬於${
          values[3]
        }區段。此數值顯示市場對其未來盈餘預期已反映於股價之中，估值評價略偏${
          values[4]
        }，建議投資人進一步觀察其基本面與產業循環位置以佐證估值合理性。`,
    },
    "what-is-the-operating-profit-of-symbol": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的營業利益是多少？`,
      content: (values: (string | number)[]) =>
        `依據最新財報，${values[0]} (${formatSymbol(
          values[1]
        )}) 本期營業利益為 ${formats.compact(
          values[2] as number
        )} 美元，營業利益率為 ${formats.percent(
          values[3] as number
        )}，與去年同期相比${values[4]} ${formats.percent(
          Math.abs(values[5] as number)
        )}。營業利益反映企業於營業收入中扣除營業成本與費用後的獲利水準，能有效衡量其本業經營效率與成本控制能力，是評估企業營運體質的核心指標之一。`,
    },
    "how-is-symbol-revenue-growth": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的營收成長如何？`,
      content: (values: (string | number)[]) =>
        `${values[0]} (${formatSymbol(
          values[1]
        )}) 在本期財報中公布營收為 ${formats.compact(
          values[2] as number
        )} 美元，年增率為 ${formats.percent(
          values[3] as number
        )}。營收成長可由產品組合變化、市占率提升、價格調整或國際市場擴張等多項因素驅動，投資人應同步觀察其毛利率與區域營收分布，以全面掌握成長品質與可持續性。`,
    },
    "how-much-debt-does-symbol-have": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的負債有多少？`,
      content: (values: (string | number)[]) =>
        `截至財報期末，${values[0]} (${formatSymbol(
          values[1]
        )}) 的總負債為 ${formats.compact(
          values[2] as number
        )} 美元，負債比率為 ${formats.decimal(values[3] as number)}，其中${
          values[4]
        }負債占比較高。財務槓桿程度對企業資本結構與利息保障倍數具有實質影響，若負債水位偏高，需關注其利息費用負擔與再融資風險；反之，低槓桿結構則顯示風險承受度較高但成長彈性可能較小。`,
    },
    "how-much-cash-does-symbol-have": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的現金有多少？`,
      content: (values: (string | number)[]) =>
        `${values[0]} (${formatSymbol(
          values[1]
        )}) 期末持有現金及約當現金總額為 ${formats.compact(
          values[2] as number
        )} 美元，占總資產比率為 ${formats.decimal(
          values[3] as number
        )}，流動比率與速動比率皆反映其短期償債能力穩健。持有高水位現金通常代表企業具備良好的資金調度彈性，可支應營運需求、擴張投資或股東回饋政策。`,
    },
    "does-symbol-go-with-three-margins-increasing": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 有符合三率三升嗎？`,
      content: (values: (string | number)[]) =>
        `${values[0]} (${formatSymbol(values[1])}) 本期財報${
          values[2]
        }「三率三升」，其中毛利率為 ${formats.percent(
          values[3] as number
        )}、營業利益率為 ${formats.percent(
          values[4] as number
        )}、淨利率為 ${formats.percent(
          values[5] as number
        )}。這顯示企業在獲利能力上${
          values[6]
        }，為基本面重點觀察的訊號。投資人可以結合三率變化與其他財務指標，全面評估 ${formatSymbol(
          values[1]
        )} 的獲利趨勢與未來成長潛力。`,
    },
    "is-symbol-eps-continuing-to-grow": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的 EPS 持續在成長嗎？`,
      content: (values: (string | number)[]) =>
        `根據近四季財報，${values[0]} (${formatSymbol(
          values[1]
        )}) 每股盈餘（EPS）呈${
          values[2]
        }趨勢，最近一期 EPS 為 ${formats.compact(
          values[3] as number
        )} 美元。若其 EPS 能在營收成長及成本優化雙重作用下穩健提升，將有利於本益比評價修復並吸引長期資金布局。`,
    },
    "what-is-the-fcf-of-symbol": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 自由現金流狀況如何？`,
      content: (values: (string | number)[]) =>
        `${values[0]} (${formatSymbol(
          values[1]
        )}) 本期自由現金流（FCF）為 ${formats.compact(
          values[2] as number
        )} 美元，係營運現金流減資本支出後之淨流量，與前期相比${
          values[3]
        } ${formats.percent(
          Math.abs(values[4] as number)
        )}。自由現金流正向成長可為股利政策、債務償還或策略性併購提供穩定資金來源，亦為評估企業真實獲利能力與股東報酬潛力的重要參考依據。`,
    },
    "what-are-the-peg-ratio-and-pe-ratio-of-symbol": {
      title: ({ symbol }: { symbol: string }) =>
        `${formatSymbol(symbol)} 的 PEG ratio 和 PE ratio 如何？`,
      content: (values: (string | number)[]) =>
        `最新估值資料顯示，${values[0]} (${formatSymbol(
          values[1]
        )}) 的本益比（PE Ratio）為 ${formats.decimal(
          values[2] as number
        )}，而 PEG Ratio（Price/Earnings to Growth）為 ${formats.decimal(
          values[3] as number
        )}。PEG 小於 1 通常代表市場低估其成長潛力，反之，PEG 大於 1 則顯示該股價中已反映高成長預期。綜合評估時，應搭配其歷史成長性、市場預期準確性及產業週期，進行全面估值分析。`,
    },
  },
  en: {
    "when-is-symbol-latest-earnings-report-released": {
      title: ({ symbol }: { symbol: string }) =>
        `When is ${formatSymbol(symbol)}'s latest earnings report released?`,
      content: (values: (string | number)[]) =>
        `The most recent financial report for ${values[0]} (${formatSymbol(
          values[1]
        )}) covers the period of ${values[2]} and was published on ${
          values[3]
        }. This report is prepared according to IFRS/US GAAP standards and includes key financial indicators—Revenue, Profitability, Cash Flow, and Capital Structure. This information is essential for investors evaluating ${formatSymbol(
          values[1]
        )}'s short-term business performance and financial health. For the latest updates on ${formatSymbol(
          values[1]
        )}'s earnings releases, visit this page regularly.`,
    },
    "where-does-symbol-fall-in-the-pe-river-chart": {
      title: ({ symbol }: { symbol: string }) =>
        `Where does ${formatSymbol(symbol)} fall in the P/E River chart?`,
      content: (values: (string | number)[]) =>
        `According to historical valuation range analysis, ${
          values[0]
        } (${formatSymbol(
          values[1]
        )})'s current price-to-earnings (P/E) ratio is ${formats.decimal(
          values[2] as number
        )}, placing it in the ${
          values[3]
        } zone on the P/E River chart. This level indicates that the market's expectations for future earnings are already reflected in the share price, with the valuation currently leaning ${
          values[4]
        }. Investors are advised to further examine the company's fundamentals and its position in the industry cycle to validate whether the valuation is justified.`,
    },
    "what-is-the-operating-profit-of-symbol": {
      title: ({ symbol }: { symbol: string }) =>
        `What is the operating profit of ${formatSymbol(symbol)}?`,
      content: (values: (string | number)[]) =>
        `According to the latest financial report, ${values[0]} (${formatSymbol(
          values[1]
        )}) reported an Operating Profit of ${formats.compact(
          values[2] as number
        )} with an Operating Margin of ${formats.percent(
          values[3] as number
        )} this period, representing a ${values[4]} of ${formats.percent(
          Math.abs(values[5] as number)
        )} compared to the same period last year. Operating Profit reflects the company's core business efficiency and cost control, making it a key indicator for evaluating operational strength and profitability.`,
    },
    "how-is-symbol-revenue-growth": {
      title: ({ symbol }: { symbol: string }) =>
        `How is ${formatSymbol(symbol)}'s revenue growth?`,
      content: (values: (string | number)[]) =>
        `In the latest financial report, ${values[0]} (${formatSymbol(
          values[1]
        )}) announced revenue of ${formats.compact(
          values[2] as number
        )}, with a Year-Over-Year growth rate of ${formats.percent(
          values[3] as number
        )}. Revenue growth can be driven by product mix changes, market share expansion, price adjustments, or international market penetration. Investors should also monitor gross margin and regional revenue distribution for a comprehensive view of growth quality and sustainability.`,
    },
    "how-much-debt-does-symbol-have": {
      title: ({ symbol }: { symbol: string }) =>
        `How much debt does ${formatSymbol(symbol)} have?`,
      content: (values: (string | number)[]) =>
        `As of the end of the reporting period, ${values[0]} (${formatSymbol(
          values[1]
        )}) had total debt of ${formats.compact(
          values[2] as number
        )}, with a debt ratio of ${formats.decimal(values[3] as number)}. ${
          values[4]
        } debt comprises a higher/lower proportion. The level of financial leverage directly impacts the company's capital structure and interest coverage. If debt is high, pay attention to interest expenses and refinancing risks. Conversely, a low-leverage structure indicates greater risk tolerance but potentially less growth flexibility.`,
    },
    "how-much-cash-does-symbol-have": {
      title: ({ symbol }: { symbol: string }) =>
        `How much cash does ${formatSymbol(symbol)} have?`,
      content: (values: (string | number)[]) =>
        `At the end of the period, ${values[0]} (${formatSymbol(
          values[1]
        )}) held Total Cash and Cash Equivalents of ${formats.compact(
          values[2] as number
        )}, accounting for ${formats.decimal(
          values[3] as number
        )} of total assets. Both current and quick ratios indicate robust short-term debt repayment ability. High cash reserves typically mean the company has strong liquidity, supporting operational needs, expansion investments, or shareholder returns.`,
    },
    "does-symbol-go-with-three-margins-increasing": {
      title: ({ symbol }: { symbol: string }) =>
        `Does ${formatSymbol(symbol)} go with three margins increasing?`,
      content: (values: (string | number)[]) =>
        `In the latest report, ${values[0]} (${formatSymbol(values[1])}) ${
          values[2]
        } the “three margins increasing” benchmark, with a gross margin of ${formats.percent(
          values[3] as number
        )}%, operating margin of ${formats.percent(
          values[4] as number
        )}%, and net margin of ${formats.percent(
          values[5] as number
        )}%. This demonstrates ${
          values[6]
        } in profitability, which is a key signal for fundamental analysis. Investors should consider margin trends alongside other financial indicators to assess ${formatSymbol(
          values[1]
        )}'s profit trajectory and future growth potential.`,
    },
    "is-symbol-eps-continuing-to-grow": {
      title: ({ symbol }: { symbol: string }) =>
        `Is ${formatSymbol(symbol)}'s EPS continuing to grow?`,
      content: (values: (string | number)[]) =>
        `According to the past four quarterly reports, ${
          values[0]
        } (${formatSymbol(values[1])})'s earnings per share (EPS) shows a ${
          values[2]
        } trend, with the latest EPS at ${formats.compact(
          values[3] as number
        )}. If EPS continues to rise due to revenue growth and cost optimization, it can support P/E valuation recovery and attract long-term investors.`,
    },
    "what-is-the-fcf-of-symbol": {
      title: ({ symbol }: { symbol: string }) =>
        `What is the FCF of ${formatSymbol(symbol)}?`,
      content: (values: (string | number)[]) =>
        `${values[0]} (${formatSymbol(
          values[1]
        )})'s Free Cash Flow (FCF) for the period is ${formats.compact(
          values[2] as number
        )}, calculated as Operating Cash Flow minus Capital Expenditures, representing a ${
          values[3]
        } of ${formats.percent(
          Math.abs(values[4] as number)
        )} compared with the previous period. Positive FCF growth provides stable funding for dividends, debt repayment, or strategic acquisitions, and is an important measure of true profitability and shareholder return potential.`,
    },
    "what-are-the-peg-ratio-and-pe-ratio-of-symbol": {
      title: ({ symbol }: { symbol: string }) =>
        `What are the PEG ratio and PE ratio of ${formatSymbol(symbol)}?`,
      content: (values: (string | number)[]) =>
        `The latest valuation data shows ${values[0]} (${formatSymbol(
          values[1]
        )}) has a Price-To-Earnings (PE) ratio of ${formats.decimal(
          values[2] as number
        )} and a Price/Earnings-To-Growth (PEG) ratio of ${formats.decimal(
          values[3] as number
        )}. A PEG below 1 usually suggests the market is underestimating growth potential, while a PEG above 1 indicates high growth expectations are already priced in. Investors should conduct a comprehensive valuation by considering historical growth, market forecasts, and industry cycles.`,
    },
  },
};
